import useSWR from "swr";
import {
  Box,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Heading,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { TickCircle, Warning2, CloseCircle } from "iconsax-react";
import baseService from "../../services/base";
import Loader from "../../components/Feedback/Loader";
import Narrative from "./Narrative";

// EG-38 (passo 8 BI) — Qualidade de dados. Reusa GET /metrics/data-quality.
// "Diga a verdade": mostra o quão confiável é a base que alimenta os dashboards.

const INK = "#292B30";
const INK_MUTED = "#5C6066";
const GOOD = "#2F855A";
const WARN = "#C05621";
const FAIL = "#C53030";
const SURFACE = "#FFFFFF";

const fetcher = (url) => baseService.get(url).then((r) => r?.data);

const STATUS = {
  ok: { color: GOOD, Icon: TickCircle },
  warn: { color: WARN, Icon: Warning2 },
  fail: { color: FAIL, Icon: CloseCircle },
};
const SEVERITY_LABEL = { high: "Alta", medium: "Média", low: "Baixa" };

function scoreColor(score) {
  if (score >= 80) return GOOD;
  if (score >= 60) return WARN;
  return FAIL;
}

export default function DataQualityDashboard() {
  const { data, isLoading } = useSWR("metrics/data-quality", fetcher);

  if (isLoading) return <Loader />;
  if (!data) {
    return (
      <Flex bg={SURFACE} borderRadius="12px" p="48px" justify="center">
        <Text color={INK_MUTED}>Não foi possível carregar a qualidade de dados.</Text>
      </Flex>
    );
  }

  const rules = data.rules || [];
  const worst = [...rules].sort((a, b) => a.percent - b.percent)[0];
  const narrative =
    worst && worst.percent < 90
      ? `Qualidade dos dados em ${data.score}%. O ponto mais crítico é "${worst.label}", em apenas ${worst.percent}% — decisões que dependem disso podem estar erradas.`
      : `Qualidade dos dados em ${data.score}%. A base que alimenta os painéis está consistente.`;

  return (
    <Box>
      <Narrative tone={data.score < 60 ? "warn" : "good"}>{narrative}</Narrative>
      <SimpleGrid columns={{ base: 1, md: "auto 1fr" }} spacing="24px" mb="24px" alignItems="center">
        {/* Score geral */}
        <Flex bg={SURFACE} borderRadius="12px" p="24px" direction="column" align="center" minW="220px">
          <Text fontSize="12px" color={INK_MUTED} mb="12px">
            Qualidade dos dados
          </Text>
          <CircularProgress
            value={data.score}
            size="150px"
            thickness="10px"
            color={scoreColor(data.score)}
            trackColor="#EDF2F7"
            capIsRound
          >
            <CircularProgressLabel fontSize="34px" fontWeight={700} color={INK}>
              {data.score}%
            </CircularProgressLabel>
          </CircularProgress>
        </Flex>

        <Box bg={SURFACE} borderRadius="12px" p={["20px", "24px"]}>
          <Heading size="sm" color={INK} mb="4px">
            Confiabilidade da base
          </Heading>
          <Text fontSize="14px" color={INK_MUTED}>
            Cada regra é checada no banco que alimenta os dashboards. Regras vermelhas indicam dado
            faltante ou inconsistente — decisões tomadas sobre elas podem estar erradas.
          </Text>
        </Box>
      </SimpleGrid>

      <Box bg={SURFACE} borderRadius="12px" p={["16px", "8px"]}>
        {rules.map((rule, i) => {
          const s = STATUS[rule.status] || STATUS.warn;
          const Icon = s.Icon;
          return (
            <Flex
              key={rule.key}
              align={["flex-start", "center"]}
              justify="space-between"
              gap="16px"
              p="16px"
              borderTop={i === 0 ? "none" : "1px solid #EDF2F7"}
              direction={["column", "row"]}
            >
              <Flex align="flex-start" gap="12px" flex="1">
                <Icon size="22" variant="Bold" color={s.color} style={{ flexShrink: 0 }} />
                <Box>
                  <Flex align="center" gap="8px" wrap="wrap">
                    <Text fontWeight={600} color={INK}>
                      {rule.label}
                    </Text>
                    <Text
                      fontSize="11px"
                      color={INK_MUTED}
                      bg="#F1F0F6"
                      px="8px"
                      py="2px"
                      borderRadius="full"
                    >
                      {SEVERITY_LABEL[rule.severity] || rule.severity}
                    </Text>
                  </Flex>
                  <Text fontSize="13px" color={INK_MUTED} mt="2px">
                    {rule.description}
                  </Text>
                </Box>
              </Flex>

              <Flex align="center" gap="16px" minW={["100%", "180px"]} justify="flex-end">
                <Text fontSize="13px" color={INK_MUTED} whiteSpace="nowrap">
                  {rule.ok}/{rule.total}
                </Text>
                <Box w="90px">
                  <Flex justify="space-between" mb="2px">
                    <Text fontSize="13px" fontWeight={700} color={s.color}>
                      {rule.percent}%
                    </Text>
                  </Flex>
                  <Box bg="#EDF2F7" borderRadius="full" h="6px" overflow="hidden">
                    <Box bg={s.color} h="6px" w={`${rule.percent}%`} borderRadius="full" />
                  </Box>
                </Box>
              </Flex>
            </Flex>
          );
        })}
      </Box>
    </Box>
  );
}
